import { motion } from 'framer-motion';
import { Check, Printer, X, XCircle } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { textStyle, typography } from '../../styles/typography';
import Card from '../ui/card';
import IconTextButton from '../ui/IconTextButton';
import './LiveOrderFeed.css';
import PrintKOTPreview, { KOTSlipContent } from './PrintKOTPreview';

// Set State dropdown: Preparing, Food Ready, Dispatched, Delivered only (no Pending, Accepted, Cancelled)
const SET_STATE_OPTIONS = [
  { value: 'Preparing', label: 'Preparing' },
  { value: 'Food Ready', label: 'Food Ready' },
  { value: 'Dispatched', label: 'Dispatched' },
  // { value: 'Delivered', label: 'Delivered' },
];

// User cannot go backwards: Food Ready -> Preparing; Dispatched -> Preparing or Food Ready
const isStateTransitionAllowed = (currentState, targetValue) => {
  if (!currentState || currentState === targetValue) return true;
  const current = (currentState || '').trim();
  const target = (targetValue || '').trim();
  if (current === 'Food Ready' && target === 'Preparing') return false;
  if (current === 'Dispatched' && (target === 'Preparing' || target === 'Food Ready')) return false;
  return true;
};

const formatOrderedAt = (timestamp) => {
  if (!timestamp) return '—';
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    dateStyle: 'short',
    timeStyle: 'short',
  });
};

// Build table rows from order (support items array or legacy dishNames + quantity)
const getOrderItems = (order) => {
  if (order?.items && Array.isArray(order.items) && order.items.length > 0) {
    return order.items.map((item) => ({
      dishName: item.dishName || item.dish_name || '—',
      quantity: item.quantity ?? 1,
      modifiers: item.modifiers || item.addons || '—',
      setState: item.setState ?? order.status ?? 'Pending',
    }));
  }
  const dishNames = order?.dishNames || order?.dish_names || '';
  const qty = order?.quantity ?? 1;
  if (!dishNames) {
    return [{ dishName: '—', quantity: qty, modifiers: '—', setState: order?.status ?? 'Pending' }];
  }
  const names = dishNames.split(',').map((s) => s.trim()).filter(Boolean);
  if (names.length === 0) {
    return [{ dishName: dishNames, quantity: qty, modifiers: '—', setState: order?.status ?? 'Pending' }];
  }
  const perQty = Math.max(1, Math.floor(qty / names.length));
  return names.map((name) => ({
    dishName: name,
    quantity: perQty,
    modifiers: '—',
    setState: order?.status ?? 'Pending',
  }));
};

const LiveOrderDetailsOverlay = ({ onClose, order, onAccept, onDecline }) => {
  const [showDeclineConfirm, setShowDeclineConfirm] = useState(false);
  const [showKOTPreview, setShowKOTPreview] = useState(false);
  const items = order ? getOrderItems(order) : [];
  const [rowStates, setRowStates] = useState(() => items.map((item) => item.setState));

  // When order becomes Accepted, set row states to Preparing so Set State dropdowns start correctly
  useEffect(() => {
    const status = (order?.status || '').trim().toLowerCase();
    if (status === 'accepted' && items.length > 0) {
      setRowStates((prev) => {
        const needsReset = prev.some(
          (s) => ((s || '').trim().toLowerCase() === 'accepted' || (s || '').trim().toLowerCase() === 'pending')
        );
        return needsReset ? items.map(() => 'Preparing') : prev;
      });
    }
  }, [order?.status, items.length]);

  if (!order) return null;
  const customerName = order.customerName ?? order.customer_name ?? order.name ?? '—';
  const statusLower = (order.status || '').trim().toLowerCase();
  const isCancelled = statusLower === 'cancelled';
  const isPending = statusLower === 'pending';
  const isAccepted = statusLower === 'accepted';
  // Allow Set State dropdown for any order in workflow: Accepted, Preparing, Food Ready, Dispatched
  const workflowStatuses = ['accepted', 'preparing', 'food ready', 'dispatched'];
  const canChangeState = !isCancelled && workflowStatuses.includes(statusLower);

  const handleAccept = () => {
    onAccept?.(order);
    // Overlay stays open; order status becomes Accepted so Set State dropdowns enable
  };

  const handleDeclineClick = () => {
    setShowDeclineConfirm(true);
  };

  const handleDeclineConfirm = () => {
    setShowDeclineConfirm(false);
    onDecline?.(order);
    // Context closes overlay after onDecline
  };

  const handleDeclineCancel = () => {
    setShowDeclineConfirm(false);
  };

  const handleSetStateChange = (index, value) => {
    if (isCancelled) return;
    const currentState = rowStates[index] ?? items[index]?.setState;
    if (!isStateTransitionAllowed(currentState, value)) return;
    setRowStates((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
    // TODO: persist per-line status if backend supports
  };

  const handleKOTPreview = () => {
    setShowKOTPreview(true);
  };

  const handlePrintKOT = () => {
    window.print();
  };

  const inputClass = 'w-full min-h-[40px] rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500';
  const labelClass = textStyle(typography.sh3, 'text-gray-600 block mb-1');

  return (
    <div className="live-order-details">
      <div className="live-order-details-header">
        <h2 className={textStyle(typography.h3, 'live-order-details-title')}>
          Order Details
        </h2>
        <button
          type="button"
          onClick={onClose}
          className="live-order-details-close"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <Card variant="light" className="live-order-details-card p-5">
        {/* Top row: Order info left, Accept/Decline right */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
          <div className="live-order-details-grid">
            <div>
              <label className={labelClass}>Order no.</label>
              <p className={typography.p} style={{ color: '#111827' }}>{order.orderNo || order.order_no || '—'}</p>
            </div>
            <div>
              <label className={labelClass}>Name</label>
              <p className={typography.p} style={{ color: '#111827' }}>{customerName}</p>
            </div>
            <div>
              <label className={labelClass}>Ordered at</label>
              <p className={typography.p} style={{ color: '#111827' }}>{formatOrderedAt(order.orderPlacedAt || order.ordered_at)}</p>
            </div>
          </div>
          {isPending && !isCancelled && (
            <div className="flex items-center gap-2 shrink-0">
              <IconTextButton
                icon={<Check className="w-5 h-5" style={{ color: '#ffffff' }} />}
                text="Accept"
                onClick={handleAccept}
                variant="default"
                size="sm"
              />
              <IconTextButton
                icon={<XCircle className="w-5 h-5" style={{ color: '#ffffff' }} />}
                text="Decline"
                onClick={handleDeclineClick}
                variant="danger"
                size="sm"
              />
            </div>
          )}
          {isCancelled && (
            <div className="shrink-0 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm font-medium text-red-800">
              Order cancelled — no changes allowed
            </div>
          )}
        </div>

        {/* Table */}
        <div className="overflow-x-auto rounded-xl border border-gray-200 mb-6">
          <table className="min-w-full border-collapse text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-gray-600">
                <th className="px-4 py-3 font-semibold">S.no</th>
                <th className="px-4 py-3 font-semibold min-w-[160px]">Dish name</th>
                <th className="px-4 py-3 font-semibold min-w-[80px]">Quantity</th>
                <th className="px-4 py-3 font-semibold min-w-[140px]">Modifiers / Add-ons</th>
                <th className="px-4 py-3 font-semibold min-w-[140px]">Set State</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => (
                <tr key={index} className="border-t border-gray-100">
                  <td className="px-4 py-3 text-gray-800">{index + 1}</td>
                  <td className="px-4 py-3 text-gray-800">{item.dishName}</td>
                  <td className="px-4 py-3 text-gray-800">{item.quantity}</td>
                  <td className="px-4 py-3 text-gray-800">{item.modifiers}</td>
                  <td className="px-4 py-3">
                    <select
                      value={SET_STATE_OPTIONS.some((o) => o.value === (rowStates[index] ?? item.setState))
                        ? (rowStates[index] ?? item.setState)
                        : 'Preparing'}
                      onChange={(e) => handleSetStateChange(index, e.target.value)}
                      disabled={!canChangeState}
                      className={inputClass + ' min-h-[36px] py-1.5 ' + (!canChangeState ? 'opacity-60 cursor-not-allowed' : '')}
                    >
                      {SET_STATE_OPTIONS.map((opt) => {
                        const currentState = rowStates[index] ?? item.setState;
                        const allowed = isStateTransitionAllowed(currentState, opt.value);
                        return (
                          <option key={opt.value} value={opt.value} disabled={!allowed}>
                            {opt.label}
                          </option>
                        );
                      })}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* KOT Preview & Print KOT */}
        <div className="flex justify-start gap-2">
          <IconTextButton
            icon={<Printer className="w-5 h-5" style={{ color: '#ffffff' }} />}
            text="KOT Preview & Print"
            onClick={handleKOTPreview}
            variant="default"
            size="sm"
          />
          {/* <IconTextButton
            icon={<Printer className="w-5 h-5" style={{ color: '#ffffff' }} />}
            text="Print KOT"
            onClick={handlePrintKOT}
            variant="default"
            size="sm"
          /> */}
        </div>
      </Card>

      {/* KOT print preview */}
      {/* Hidden KOT slip for direct "Print KOT" – only printed, not shown on screen */}
      {!showKOTPreview && order && (
        <div id="kot-print-only" className="kot-print-only-hidden" aria-hidden="true">
          <KOTSlipContent order={order} items={items} />
        </div>
      )}

      {showKOTPreview &&
        createPortal(
          <PrintKOTPreview
            order={order}
            items={items}
            onClose={() => setShowKOTPreview(false)}
          />,
          document.body
        )}

      {/* Decline confirmation overlay */}
      {showDeclineConfirm && (
        <motion.div
          className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={(e) => e.target === e.currentTarget && handleDeclineCancel()}
        >
          <motion.div
            className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl"
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className={typography.h3} style={{ color: '#111827', marginBottom: '0.5rem' }}>Decline order?</h3>
            <p className={typography.p} style={{ color: '#374151', marginBottom: '1.5rem' }}>
              Are you sure you want to decline this order? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={handleDeclineCancel}
                className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 text-sm font-medium"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeclineConfirm}
                className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 text-sm font-medium"
              >
                Yes, Decline
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default LiveOrderDetailsOverlay;
